import { useMemo, useState } from "react";
import { AtomBadge } from "./primitives";

/**
 * Polar-layout "force-directed style" SVG network graph.
 * - Central node = subject
 * - Surrounding nodes = counterparties, related cases, sanctions hits, mule accounts
 * - Edges with weights, hover to highlight
 */
export function NetworkGraph({ subject, nodes = [], edges = [], width = 720, height = 420 }) {
  const [hoverId, setHoverId] = useState(null);
  const cx = width / 2;
  const cy = height / 2;

  // Place subject at centre, others on rings by group
  const ringRadii = { primary: 140, secondary: 175, outer: 195 };
  const groups = useMemo(() => {
    const g = {};
    nodes.forEach((n) => { (g[n.ring || "primary"] = g[n.ring || "primary"] || []).push(n); });
    return g;
  }, [nodes]);

  const positioned = useMemo(() => {
    const out = [{ id: "subject", x: cx, y: cy, ...subject, isSubject: true }];
    Object.entries(groups).forEach(([ring, items]) => {
      const r = ringRadii[ring] || 140;
      const step = (2 * Math.PI) / items.length;
      items.forEach((n, i) => {
        const angle = i * step - Math.PI / 2 + (ring === "secondary" ? Math.PI / items.length : 0);
        out.push({ ...n, x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
      });
    });
    return out;
  }, [groups, cx, cy, subject]);

  const findPos = (id) => positioned.find((p) => p.id === id);

  const nodeColor = (n) => {
    if (n.isSubject) return "#0F172A";
    if (n.kind === "sanctions") return "#991B1B";
    if (n.kind === "pep") return "#B7791F";
    if (n.kind === "mule") return "#C2410C";
    if (n.kind === "case") return "#F59A2D";
    if (n.kind === "fx") return "#1E3A8A";
    return "#475569";
  };

  return (
    <div className="relative">
      <svg width="100%" viewBox={`0 0 ${width} ${height}`} className="select-none">
        <defs>
          <radialGradient id="bgGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F59A2D" stopOpacity="0.10" />
            <stop offset="60%" stopColor="#F59A2D" stopOpacity="0.02" />
            <stop offset="100%" stopColor="#F59A2D" stopOpacity="0" />
          </radialGradient>
          <marker id="arrow" markerWidth="6" markerHeight="6" refX="6" refY="3" orient="auto">
            <path d="M0,0 L6,3 L0,6 z" fill="#475569" opacity="0.5" />
          </marker>
        </defs>

        {/* Background glow around subject */}
        <circle cx={cx} cy={cy} r={210} fill="url(#bgGlow)" />

        {/* Ring guides */}
        {[ringRadii.primary, ringRadii.secondary].map((r, i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke="#E8E5DC" strokeDasharray="2 4" strokeWidth="0.5" />
        ))}

        {/* Edges (curved) */}
        {edges.map((e, i) => {
          const s = findPos(e.from);
          const t = findPos(e.to);
          if (!s || !t) return null;
          const isHover = hoverId && (hoverId === e.from || hoverId === e.to);
          const mx = (s.x + t.x) / 2;
          const my = (s.y + t.y) / 2;
          // Curve perpendicular to chord
          const dx = t.x - s.x, dy = t.y - s.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          const offset = Math.min(40, len * 0.15);
          const px = -dy / len * offset;
          const py = dx / len * offset;
          return (
            <g key={i}>
              <path
                d={`M${s.x},${s.y} Q${mx + px},${my + py} ${t.x},${t.y}`}
                stroke={isHover ? "#F59A2D" : "#94A3B8"}
                strokeWidth={isHover ? 1.5 : (e.weight ? Math.min(2, e.weight) : 0.8)}
                fill="none"
                opacity={isHover ? 1 : 0.4}
              />
              {e.label && isHover && (
                <text x={mx + px} y={my + py - 4} textAnchor="middle" fontSize="9" fill="#161614" fontWeight="500">
                  {e.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Nodes */}
        {positioned.map((n) => {
          const isSub = n.isSubject;
          const isHov = hoverId === n.id;
          const r = isSub ? 28 : isHov ? 16 : 12;
          return (
            <g key={n.id} onMouseEnter={() => setHoverId(n.id)} onMouseLeave={() => setHoverId(null)} style={{ cursor: "pointer" }}>
              {isSub && <circle cx={n.x} cy={n.y} r={r + 6} fill="none" stroke="#F59A2D" strokeWidth="1" opacity="0.4">
                <animate attributeName="r" values={`${r + 6};${r + 12};${r + 6}`} dur="3s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.4;0.05;0.4" dur="3s" repeatCount="indefinite" />
              </circle>}
              <circle cx={n.x} cy={n.y} r={r} fill={nodeColor(n)} stroke="#FFFFFF" strokeWidth="2" />
              {isSub && (
                <text x={n.x} y={n.y + 4} textAnchor="middle" fontSize="11" fill="#FFFFFF" fontWeight="600">{(n.label || "S").slice(0, 2).toUpperCase()}</text>
              )}
              <text x={n.x} y={n.y + r + 12} textAnchor="middle" fontSize="9.5" fill="#161614" fontWeight={isHov || isSub ? "600" : "400"}>
                {(n.label || "").length > 18 ? (n.label || "").slice(0, 17) + "…" : (n.label || "")}
              </text>
              {n.sub && <text x={n.x} y={n.y + r + 23} textAnchor="middle" fontSize="8" fill="#8A877E">{n.sub}</text>}
            </g>
          );
        })}
      </svg>

      {/* Hover detail card */}
      {hoverId && hoverId !== "subject" && (() => {
        const n = positioned.find((p) => p.id === hoverId);
        if (!n) return null;
        return (
          <div className="absolute top-2 right-2 bg-white border border-line rounded-lg p-2.5 shadow-sm w-[220px]">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: nodeColor(n) }} />
              <span className="text-[11px] font-medium truncate">{n.label}</span>
            </div>
            {n.sub && <div className="text-[10px] text-ink-subtle mt-0.5">{n.sub}</div>}
            {n.detail && <div className="text-[10px] text-ink-muted mt-1.5">{n.detail}</div>}
          </div>
        );
      })()}

      {/* Legend */}
      <div className="flex items-center justify-center gap-3 mt-2 text-[10px] text-ink-subtle flex-wrap">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#0F172A]" />Subject</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#475569]" />Counterparty</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#F59A2D]" />Related case</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#991B1B]" />Sanctions</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#B7791F]" />PEP</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#C2410C]" />Mule</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#1E3A8A]" />FX corridor</span>
        <span className="ml-2 text-[9px] text-ink-subtle">Generated by</span>
        <AtomBadge model="Atom Nucleus" version="v3.5" />
      </div>
    </div>
  );
}

/** Build a synthetic-but-deterministic graph for a subject ID. */
export function graphForSubject(subjectName, subjectId, typology = "T1003", amount = 0) {
  const counterparties = [
    { id: "cp1", label: "Sola Danjuma", sub: "Crystal Commerce", kind: "counterparty", ring: "primary", detail: "12 transactions · ₦18.4M last 30d" },
    { id: "cp2", label: "Halimah Sani", sub: "Northwind Bank", kind: "counterparty", ring: "primary", detail: "8 transactions · ₦9.2M last 30d" },
    { id: "cp3", label: "Tunde Bamidele", sub: "Helios Bank", kind: "mule", ring: "primary", detail: "Newly-opened · 4 inflows" },
    { id: "cp4", label: "ZipPay Wallet", sub: "Fintech", kind: "fx", ring: "secondary", detail: "₦17.5M wallet topup" },
    { id: "cp5", label: "Aurora Holdings", sub: "Northwind London", kind: "counterparty", ring: "secondary", detail: "USD 25K outbound" },
    { id: "cs1", label: "CSE-2026-00126", sub: "Folake Ogundipe", kind: "case", ring: "secondary", detail: "Round-trip layering · open" },
    { id: "cs2", label: "CSE-2026-00128", sub: "Linked via Heritage Trust", kind: "case", ring: "secondary", detail: "Smurfing ring · P1" },
    { id: "san1", label: "OFAC SDN-00284", sub: "92% fuzzy match", kind: "sanctions", ring: "outer", detail: "Hit on outbound wire" },
    { id: "pep1", label: "Sani Lawal", sub: "Local Govt Chairman", kind: "pep", ring: "outer", detail: "PEP behavioural deviation" },
  ];
  const edges = [
    { from: "subject", to: "cp1", weight: 1.6, label: "12 txns" },
    { from: "subject", to: "cp2", weight: 1.2, label: "8 txns" },
    { from: "subject", to: "cp3", weight: 0.9, label: "Mule link" },
    { from: "subject", to: "cp4", weight: 1.4, label: "Wallet" },
    { from: "cp4", to: "cp5", weight: 1.0, label: "FX route" },
    { from: "subject", to: "cs1", weight: 0.8, label: "Same network" },
    { from: "cs2", to: "cp1", weight: 0.7, label: "Cross-link" },
    { from: "cp5", to: "san1", weight: 0.6, label: "Sanctions" },
    { from: "subject", to: "pep1", weight: 0.5, label: "PEP corridor" },
  ];
  return {
    subject: { label: subjectName || "Subject", sub: subjectId, kind: "subject" },
    nodes: counterparties,
    edges,
  };
}
